import { useEffect, useRef } from "react";
import { useGetAppVersion } from "./queries";
import { showVersionControlToast } from "./toast";
import { isDialerWidgetOpen } from "@/helpers/widgets";
import { LocalStorage } from "@/helpers/local-storage";
import { APP_VERSION } from "@/lib/constants/version";
import { isVersionNew } from "@/helpers/version";
import { TIME_INTERVALS } from "shared/lib/constants/time";
import { DayJs } from "shared/lib/helpers/date";
import { dd } from "@/helpers/datadog";
import { LOG_CATEGORIES } from "@/constants/logs";

/**
 * useVersionControl Hook
 *
 * This hook manages version control for the application:
 * - Fetches the current app version from the server
 * - Compares it with the local APP_VERSION constant
 * - Triggers a page reload or shows a toast notification if a new version is available
 * - Implements a cooldown period to prevent frequent reloads
 * - Checks for updates every minute
 *
 * To update version on staging
 * POST https://dev-api.glencoco.com/api/v1/frontend_version
 * body: { "version": "0.0.0" }
 *
 * To update version on prod
 * POST https://api.glencoco.com/api/v1/frontend_version
 * body: { "version": "0.0.0" }
 *
 * Auth token is in 1Password (Frontend Version Control)
 *
 * Can use either cURL or Postman to update the version
 */
export const useVersionControl = () => {
  const { data: appVersion } = useGetAppVersion();

  const appVersionRef = useRef(appVersion);

  useEffect(() => {
    appVersionRef.current = appVersion;
  }, [appVersion]);

  useEffect(() => {
    const checkVersion = () => {
      const appVersion = appVersionRef.current;
      const LS = new LocalStorage();

      /**
       * If either variable is not set, do nothing
       * mostlikely network or dev error
       */
      if (!appVersion || !APP_VERSION) {
        return;
      }

      /**
       * Version is up to date
       */
      if (appVersion === APP_VERSION) {
        return;
      }

      /**
       * If the app has been reloaded in the last 10 minutes, do nothing
       * This is to avoid spamming users with the version control toasts
       * or force reloads or if error occurs
       */
      if (LS.appVersionLastReloadAt) {
        const lastReloadAt = DayJs(LS.appVersionLastReloadAt);
        const now = DayJs();

        const diffMinutes = now.diff(lastReloadAt, "minute");

        if (diffMinutes < 10) {
          return;
        }
      }

      /**
       * Version is different
       */
      if (appVersion !== APP_VERSION && isVersionNew(appVersion, APP_VERSION)) {
        /**
         * If the dialer widget is open or the user is on the assessment page,
         * show the version control toast only
         */
        if (
          isDialerWidgetOpen() ||
          window.location.href.includes("/assessment/")
        ) {
          showVersionControlToast(appVersion);
          return;
        }

        LS.appVersionLastReloadAt = new Date().toISOString();

        const message = `${LOG_CATEGORIES.VERSION} force auto-reload page v${APP_VERSION} -> v${appVersion}`;
        const logData = {
          data: {
            oldVersion: APP_VERSION,
            newVersion: appVersion,
          },
        };
        dd.log(message, logData);
        dd.rum.log(message, logData);

        window.location.reload();
      }
    };

    // Run initial check
    checkVersion();

    // Set up interval to check every 60 seconds
    const interval = setInterval(checkVersion, TIME_INTERVALS.ONE_MINUTE);

    // Cleanup interval on unmount
    return () => clearInterval(interval);
  }, []);
};
